import behaviourConstants from "@/constants/behaviourManagementConstants.js"
import {
  mapCategoryColorToIndicator,
  resolveCategoryColor
} from "@/helpers/behaviourCoreValueForm.js"

/**
 * Loads core values for select dropdowns (filters, forms).
 * @param {import('vuex').Store} store
 * @returns {Promise<Array<{ label: string, value: number }>>}
 */
export async function fetchCoreValueSelectOptions(store) {
  const { rows } = await store.dispatch(
    "behaviourSettings/fetchCoreValuesList",
    {
      page: 1,
      perPage: behaviourConstants.CORE_VALUE_TABLE_ALL_ENTRIES_PER_PAGE,
      search: "",
      statusFilter: "all"
    }
  )
  return (rows || [])
    .filter((row) => row?.id != null)
    .map((row) => ({
      label: String(row?.name ?? row?.id ?? ""),
      value: row.id
    }))
}

/**
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
export function mapApiCoreValueToRow(item) {
  const category = String(item?.category ?? "").trim()
  const categoryColor = resolveCategoryColor(category)
  const statusNum = Number(item?.status)
  const status = statusNum === 0 ? "inactive" : "active"

  return {
    id: item?.id,
    name: String(item?.name ?? ""),
    description: item?.description ?? "",
    category,
    category_color: categoryColor,
    indicator_color: categoryColor
      ? mapCategoryColorToIndicator(categoryColor)
      : undefined,
    sort_order: item?.sort_order ?? null,
    status
  }
}

/** Allowed values for the core values list `sort` query param. */
export const CORE_VALUE_SORT_FIELDS = Object.freeze([
  "name",
  "description",
  "status"
])

/** Allowed values for the core values list `order` query param. */
export const CORE_VALUE_SORT_ORDERS = Object.freeze(["asc", "desc"])

/**
 * @param {{
 *   page?: number,
 *   perPage?: number,
 *   search?: string,
 *   statusFilter?: string,
 *   sortBy?: string|null,
 *   sortOrder?: string|null
 * }} args
 * @returns {Record<string, string | number>}
 */
export function buildCoreValuesListParams({
  page = 1,
  perPage = 25,
  search = "",
  statusFilter = "all",
  sortBy = null,
  sortOrder = null
} = {}) {
  const params = {
    page: Math.max(1, Number(page) || 1),
    per_page: Math.max(1, Number(perPage) || 25)
  }

  const q = String(search ?? "").trim()
  if (q) {
    params.search = q
  }

  if (statusFilter === "active") {
    params.status = 1
  } else if (statusFilter === "inactive") {
    params.status = 0
  }

  const sortByValue = String(sortBy ?? "").trim()
  const sortOrderValue = String(sortOrder ?? "")
    .trim()
    .toLowerCase()
  if (
    CORE_VALUE_SORT_FIELDS.includes(sortByValue) &&
    CORE_VALUE_SORT_ORDERS.includes(sortOrderValue)
  ) {
    params.sort = sortByValue
    params.order = sortOrderValue
  }

  return params
}

/**
 * @param {Record<string, unknown> | null | undefined} pagination
 * @param {{ perPageLabel?: string, perPageValue?: number }} [options]
 */
export function buildPaginationFromApiResponse(pagination, options = {}) {
  const total = Number(pagination?.total_records) || 0
  const activePage = Math.max(1, Number(pagination?.current_page) || 1)
  const perPage =
    Number(pagination?.per_page) || Number(options?.perPageValue) || 25
  const pages = Math.max(1, Number(pagination?.total_pages) || 1)
  const perPageLabel = options?.perPageLabel ?? String(perPage)
  const from = total === 0 ? 0 : (activePage - 1) * perPage + 1
  const to = Math.min(activePage * perPage, total)

  return {
    activePage,
    total,
    pages,
    from,
    to,
    per_page: { label: perPageLabel, value: perPage }
  }
}
